/**
 * 待办 mqtt
 */
import React, { useEffect, memo, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import Paho from 'paho-mqtt';
import { storage } from '@/utils';
import { getClientConfig } from '@/api/mqtt';
import { createStateSelector } from '@/redux/actions/reservation/helper';
import { refreshTodoList } from '@/redux/actions/todoTask/index';
import { ReducerState, storeName } from '@/redux/reducers/todoTask';

import AudioPlayer, { TaskAudioGetter, isCustomAudioType } from './Player';

const stateSelector = (state: ReducerState) => {
  const { todoListData } = state;
  return { todoListData };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callRefreshList: refreshTodoList,
};
type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

interface PahoConfig {
  username: string; // Authentication username
  password: string; // Authentication password
  brokerPort: string; // port number to connect to
  brokerAddress: string; // address of the messaging server
  clientId: string; // Messaging client identifier
  topic: string;
  publishTopic: string;
}

interface MQTTMessage {
  topic: string;
  payloadString: string;
}

const Index: React.FC<MapStateProps & MapDispatchProps> = (props) => {
  const { todoListData, callRefreshList } = props;

  // 最新的待办创建时间
  const latestTaskTime = useRef<number>();
  // 轮询时间间隔 默认60s
  const loopIntervalValue = useRef(60 * 1000);

  // 更新待办
  const updateTodoList = useMemo(() => {
    let debounceTimer;
    // 1000ms内只触发一次
    return () => {
      if (debounceTimer) return;
      debounceTimer = window.setTimeout(() => {
        callRefreshList();
        debounceTimer = null;
      }, 1000);
    };
  }, []);

  // 定时任务
  const loopTask = useMemo(() => {
    let loopTimer: number;
    // 清除定时任务
    const clearTimer = () => {
      if (loopTimer) {
        clearTimeout(loopTimer);
      }
    };
    // 设置新定时任务
    const startTimer = () => {
      clearTimer();
      loopTimer = window.setTimeout(() => {
        updateTodoList();
      }, loopIntervalValue.current);
    };
    return {
      clearTimer,
      startTimer,
    };
  }, []);

  const audioPlayer = useMemo(() => {
    latestTaskTime.current = Date.now();
    return new AudioPlayer();
  }, []);

  useEffect(() => {
    let client; // 实例

    // 降级为轮询模式
    const useBackUpMode = () => {
      loopIntervalValue.current = 10 * 1000;
      updateTodoList();
    };

    const initClient = (config: PahoConfig) => {
      return new Promise<void>((resolve, reject) => {
        const { brokerAddress, clientId, username, password, topic } = config;
        client = new Paho.Client(brokerAddress, 443, clientId);

        // 新消息推送 重新拉取待办列表
        const handleMessageReceived = (mqttMsg: MQTTMessage) => {
          loopTask.clearTimer();
          updateTodoList();
        };

        // 断开回调
        const handleConnectionLost = (event) => {
          if (event && event.errorCode !== 0) {
            // 降级为轮询
            useBackUpMode();
          }
        };

        // 新消息推送
        client.onMessageArrived = handleMessageReceived;
        // 链接断开
        client.onConnectionLost = handleConnectionLost;

        // 建立连接
        client.connect({
          timeout: 10, // 连接超时时长
          userName: username,
          password,
          cleanSession: true,
          useSSL: true,
          onSuccess: () => {
            // 订阅消息
            client.subscribe(topic);
            resolve();
          },
          onFailure: () => {
            reject();
          },
        });
      });
    };

    // 如果是集团登录 不需要待办mqtt消息
    if (!storage.get('isGroupLogin')) {
      getClientConfig()
        .then((res) => {
          if (res.data) {
            return initClient(res.data);
          }
          throw new Error('mqtt get config failed');
        })
        .catch(() => {
          console.warn('mqtt init error');
          // 初始化失败 降级为轮询模式
          useBackUpMode();
        });
    }

    return () => {
      try {
        loopTask.clearTimer();
        if (client) {
          client.disconnect();
        }
      } catch (e) {
        //
      }
    };
  }, []);

  useEffect(() => {
    // 待办列表数据有变动
    loopTask.startTimer();
    const listCount = todoListData?.length;
    if (listCount) {
      // 上次列表中最新的创建时间
      const latestTime = latestTaskTime.current;
      let lastTime = latestTime; // 当前列表中最新创建时间 默认取上次的
      let loopIndex = 0;
      while (loopIndex < listCount) {
        const itemData = todoListData[loopIndex]; // 待办详情
        const { type, createTime, content, id } = itemData; // 类型和创建时间
        // 过滤创建时间为空的脏数据
        if (createTime) {
          const itemAudioGetter = TaskAudioGetter[type]; // 获取对应音频
          lastTime = Math.max(lastTime, createTime); // 取最新创建时间
          // 目标待办任务类型
          if (typeof itemAudioGetter === 'function') {
            // 待办任务 晚于 上次获取的最早时间
            if (createTime > latestTime) {
              const audioType = itemAudioGetter(itemData);
              if (audioType) {
                audioPlayer.addTask(audioType, content, id);
              }
            }
          }
        }
        loopIndex += 1;
      }
      latestTaskTime.current = lastTime;
    }
  }, [todoListData]);

  return null;
};

export default connect<MapStateProps, MapDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(memo(Index));
